import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { PageComponentsFormFields, PageComponentsFormInputs, PageComponentsQueryKeys, useEditComponentsPageMutation, useGetByIdComponentsPageQuery } from '@/modules/Pages';

import { PageComponentsFormView } from './PageComponentsFormView';
import { pageComponentsFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  componentId: string;
}

export const PageComponentsEditFormContainer: FC<Props> = memo(({ componentId, siteId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<PageComponentsFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PageComponentsFormInputs>>(null);

  const { data: layoutData, isLoading: isLayoutLoading, error: layoutError, isError: isLayoutError } = useGetByIdComponentsPageQuery(componentId, siteId)();

  const { mutate: editComponentMutate, isLoading: isEditComponentLoading } = useEditComponentsPageMutation(componentId, siteId)({
    onSuccess: async () => {
      appMessageApi.success('Компонент успішно оновлено!');

      await queryClient.refetchQueries([PageComponentsQueryKeys.GetAll, siteId], { exact: true });

      navigate(AppRoutes.PageComponentsById.replace(':siteId', siteId).replace(':componentId', componentId));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof PageComponentsFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: PageComponentsFormFields = {
    name: layoutData?.name,
    content: layoutData?.content
  };

  const handleFinish = useCallback((values: PageComponentsFormFields): void => {
    setFormErrors(null);

    editComponentMutate(values);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.PageComponentsById.replace(':siteId', siteId).replace(':componentId', componentId));
  }, [siteId, componentId]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <FallbackedLayout
      isLoading={ isLayoutLoading }
      isError={ isLayoutError }
      error={ layoutError }
    >
      {
        () => (
          <PageComponentsFormView
            form={ form }
            initialValues={ initialValues }
            formErrors={ formErrors }
            formName="page-components-edit-form"
            formType="edit"
            handleBack={ handleBack }
            handleFinish={ handleFinish }
            handleFinishFailed={ handleFinishFailed }
            isFormLoading={ isEditComponentLoading }
            validationSchema={ pageComponentsFormValidationSchema }
          />
        )
      }
    </FallbackedLayout>
  );
});
